import client from './client'
import { buildListParams } from '../utils/pagination'

export function fetchEvents({ from, to, page, perPage } = {}) {
  const params = buildListParams({ page, perPage, extra: { from, to } })
  return client.get('/api/v1/events', { params })
}

export function fetchPublicEvents({ sort = 'new', from, to, page, perPage } = {}) {
  const params = buildListParams({ page, perPage, extra: { sort, from, to } })
  return client.get('/api/v1/public_events', { params })
}

export function fetchUserPublicEvents(userId) {
  return client.get(`/api/v1/users/${userId}/public_events`)
}

export function fetchUserPublicEventsByUsername(username) {
  return client.get(`/api/v1/u/${encodeURIComponent(username)}/events`)
}

export function fetchEvent(id) {
  return client.get(`/api/v1/events/${id}`)
}

function appendEventFields(formData, payload) {
  if (payload.name !== undefined) formData.append('event[name]', payload.name)
  if (payload.description !== undefined) formData.append('event[description]', payload.description)
  if (payload.starts_at !== undefined) formData.append('event[starts_at]', payload.starts_at)
  if (payload.ends_at !== undefined) formData.append('event[ends_at]', payload.ends_at)
  if (payload.public !== undefined) formData.append('event[public]', payload.public ? '1' : '0')
  if (payload.allow_comments !== undefined) {
    formData.append('event[allow_comments]', payload.allow_comments ? '1' : '0')
  }
  if (payload.venue !== undefined) formData.append('event[venue]', payload.venue ?? '')
  if (payload.venue_lat !== undefined) {
    formData.append('event[venue_lat]', payload.venue_lat == null ? '' : String(payload.venue_lat))
  }
  if (payload.venue_lng !== undefined) {
    formData.append('event[venue_lng]', payload.venue_lng == null ? '' : String(payload.venue_lng))
  }
  if (payload.track_ids !== undefined) {
    payload.track_ids.forEach((trackId) => formData.append('event[track_ids][]', trackId))
  }
}

export function createEvent(payload, { coverPhoto } = {}) {
  if (!coverPhoto) {
    return client.post('/api/v1/events', { event: payload })
  }

  const formData = new FormData()
  appendEventFields(formData, payload)
  formData.append('cover_photo', coverPhoto)
  return client.post('/api/v1/events', formData)
}

export function updateEvent(id, payload, { coverPhoto, removeCoverPhoto } = {}) {
  if (!coverPhoto && !removeCoverPhoto) {
    return client.patch(`/api/v1/events/${id}`, { event: payload })
  }

  const formData = new FormData()
  appendEventFields(formData, payload)
  if (coverPhoto) formData.append('cover_photo', coverPhoto)
  if (removeCoverPhoto) formData.append('remove_cover_photo', '1')
  return client.patch(`/api/v1/events/${id}`, formData)
}

export function deleteEvent(id) {
  return client.delete(`/api/v1/events/${id}`)
}

export function updateEventVisibilitySettings(eventId, isPublic) {
  return client.patch(`/api/v1/events/${eventId}/visibility_settings`, {
    public: isPublic,
  })
}

export function favoriteEvent(eventId) {
  return client.post(`/api/v1/events/${eventId}/favorite`)
}

export function unfavoriteEvent(eventId) {
  return client.delete(`/api/v1/events/${eventId}/favorite`)
}

export function fetchFavoriteEvents({ page, perPage } = {}) {
  const params = buildListParams({ page, perPage })
  return client.get('/api/v1/favorites/events', { params })
}

export function fetchSubscribedEvents({ page, perPage } = {}) {
  const params = buildListParams({ page, perPage })
  return client.get('/api/v1/subscriptions/events', { params })
}

export function subscribeToEvent(eventId) {
  return client.post(`/api/v1/events/${eventId}/subscription`)
}

export function unsubscribeFromEvent(eventId) {
  return client.delete(`/api/v1/events/${eventId}/subscription`)
}

export function joinEvent(eventId) {
  return client.post(`/api/v1/events/${eventId}/participation`)
}

export function leaveEvent(eventId) {
  return client.delete(`/api/v1/events/${eventId}/participation`)
}

export function setEventReaction(eventId, value) {
  return client.put(`/api/v1/events/${eventId}/reaction`, { value })
}

export function removeEventReaction(eventId) {
  return client.delete(`/api/v1/events/${eventId}/reaction`)
}

export function fetchEventComments(eventId, { page, perPage } = {}) {
  const params = buildListParams({ page, perPage })
  return client.get(`/api/v1/events/${eventId}/comments`, { params })
}

export function fetchPublicEventComments({ page, perPage } = {}) {
  const params = buildListParams({ page, perPage })
  return client.get('/api/v1/public_event_comments', { params })
}

export function createEventComment(eventId, formData) {
  return client.post(`/api/v1/events/${eventId}/comments`, formData)
}

export function deleteEventComment(eventId, commentId) {
  return client.delete(`/api/v1/events/${eventId}/comments/${commentId}`)
}

export function updateEventCommentsSettings(eventId, allowComments) {
  return client.patch(`/api/v1/events/${eventId}/comments_settings`, {
    allow_comments: allowComments,
  })
}

export function setEventCommentReaction(eventId, commentId, value) {
  return client.put(`/api/v1/events/${eventId}/comments/${commentId}/reaction`, { value })
}

export function removeEventCommentReaction(eventId, commentId) {
  return client.delete(`/api/v1/events/${eventId}/comments/${commentId}/reaction`)
}
